/**
 * Lavender tattoos — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tattoo_subjects_ii`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const lavenderTattoosPack: ToilePack = {
    slug: 'lavender-tattoos',
    category: 'tattoo',
    name: 'Lavender tattoos',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'bundle', label: 'Lavender bundle', subject: 'a bundle of lavender stems tied with a ribbon bow, the flower spikes fanning out at the top'},
        {key: 'sprig', label: 'Single sprig', subject: 'a single lavender sprig, the small buds stacked in tiers up the stem'},
        {key: 'bee', label: 'Lavender and bee', subject: 'two lavender stems leaning together with a honeybee landing on the tallest flower spike'},
    ],
    styles: [
        {key: 'fineline', label: 'Fine line', style: 'a single-needle fine line tattoo drawing, one uniform hairline stroke, delicate detail and almost no shading', palette: 'black ink only', framing: 'centered with a wide white margin'},
        {key: 'dotwork', label: 'Dotwork', style: 'a dotwork tattoo drawing, a thin outline with all shading built from stippled dots and dot gradients', palette: 'black ink only', framing: 'centered with a wide white margin'},
        {key: 'traditional', label: 'Traditional', style: 'American traditional tattoo flash, bold black outlines, heavy black shading and a limited flat palette', palette: 'red, yellow and green accents with black', framing: 'centered with a wide white margin'},
    ],
    directions: ['a bee', 'purple ink', 'ankle'],
    directionHint: 'try: a bee',
    seo: {
        title: 'Lavender tattoos: bundle, single sprig, bee',
        metaDescription: 'Nine lavender tattoo designs: a ribboned bundle, one sprig and a stem with a honeybee at its tip, in three styles.',
        h1: 'Lavender tattoo designs: a bundle, a single sprig, a bee',
        tagline: 'Three designs, each drawn in fine line, dotwork and American traditional on white paper.',
        intro: "Lavender is worn for calm, for a grandmother's garden or for a summer in Provence, as slim stems on the forearm, the ankle or behind the shoulder. These nine show a ribboned bundle of stems, a single sprig with its buds stacked in tiers, and a honeybee on a stem. Each is drawn as fine line, dotwork or American traditional. Type a different plant or a twist, such as a butterfly, and the three styles carry the request.",
        useCases: [
            'a slim forearm or ankle design to take to an artist',
            'a garden print for a kitchen wall',
            'a gift card for a grandmother',
            'a sprig to sketch on a notebook',
        ],
        faq: [
            {q: 'What is in the nine designs?', a: 'A bundle of lavender stems tied with ribbon, a single sprig, and two stems with a honeybee. Each one is drawn in three styles.'},
            {q: 'Can I print these?', a: 'Each design sits on a plain white background in dark line work, so it reads clearly on paper.'},
            {q: 'What is the difference between the three styles?', a: 'Fine line is one hairline stroke with almost no shading. Dotwork builds its shading from dots. American traditional has bold black outlines, heavy shading and flat red, yellow and green.'},
            {q: 'Can I ask for a different subject?', a: 'Yes. Type the subject or twist you want, such as a butterfly on the stem. The nine shown are samples, and a request is not a promised result.'},
        ],
    },
}
